import type { HTMLAttributes } from "react";
import { cn } from "../lib/cn";

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** `sm` para badge/linha de tabela, `md` para card/tile. */
  radius?: "sm" | "md" | "lg" | "full";
}

/**
 * Bloco de carregamento. Regra: o skeleton tem que ter o MESMO formato do
 * conteúdo final (mesma altura de linha, mesmo grid) — senão a tela "pula"
 * quando o dado chega e parece mais lenta do que é.
 */
export function Skeleton({ className, radius = "sm", ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "animate-pulse bg-muted [animation-duration:1.5s] motion-reduce:animate-none",
        radius === "sm" && "rounded-sm",
        radius === "md" && "rounded-md",
        radius === "lg" && "rounded-lg",
        radius === "full" && "rounded-full",
        className,
      )}
      {...props}
    />
  );
}

export interface SkeletonTextProps extends HTMLAttributes<HTMLDivElement> {
  /** Quantas linhas. A última sai mais curta, como texto real. */
  lines?: number;
}

export function SkeletonText({ lines = 3, className, ...props }: SkeletonTextProps) {
  return (
    <div className={cn("flex flex-col gap-2", className)} {...props}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={cn("h-3", index === lines - 1 ? "w-3/5" : "w-full")}
        />
      ))}
    </div>
  );
}

/**
 * Região que anuncia "carregando" uma única vez para leitor de tela,
 * em vez de deixar o usuário cego sem nenhum sinal.
 */
export function SkeletonRegion({
  label = "Carregando conteúdo",
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & { label?: string }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className={className} {...props}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}
